if (!customElements.get('koota-home-carousel')) {
  class KootaHomeCarousel extends HTMLElement {
    constructor() {
      super();
      this.current = 0;
      this.timer = null;
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.touchStartX = 0;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-koota-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-koota-dot]'));
      this.previousButton = this.querySelector('[data-koota-previous]');
      this.nextButton = this.querySelector('[data-koota-next]');
      this.pauseButton = this.querySelector('[data-koota-pause]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.interval = Math.max(Number(this.dataset.interval) || 7000, 4000);
      this.style.setProperty('--koota-carousel-duration', `${this.interval}ms`);

      const headerWrapper = document.querySelector('.header-wrapper--koota-home');
      this.headerSection = headerWrapper && headerWrapper.closest('.section-header');
      if (this.headerSection) this.headerSection.classList.add('section-header--koota-home');

      if (this.slides.length < 2) return;

      this.previousButton?.addEventListener('click', () => this.show(this.current - 1, true));
      this.nextButton?.addEventListener('click', () => this.show(this.current + 1, true));
      this.pauseButton?.addEventListener('click', () => this.togglePause());
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => {
          this.hoverPaused = true;
          this.syncPlayback();
        });
        this.addEventListener('mouseleave', () => {
          this.hoverPaused = false;
          this.syncPlayback();
        });
        this.addEventListener('focusin', () => {
          this.focusPaused = true;
          this.syncPlayback();
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) {
            this.focusPaused = false;
            this.syncPlayback();
          }
        });
      }

      this.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches[0].clientX;
      }, { passive: true });
      this.addEventListener('touchend', (event) => {
        const distance = event.changedTouches[0].clientX - this.touchStartX;
        if (Math.abs(distance) < 48) return;
        this.show(this.current + (distance < 0 ? 1 : -1), true);
      }, { passive: true });

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.reduceMotion.addEventListener?.('change', this.onReducedMotionChange);
      this.updateFocusableElements();
      this.syncPlayback();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.reduceMotion.removeEventListener?.('change', this.onReducedMotionChange);
      this.headerSection?.classList.remove('section-header--koota-home');
    }

    show(index, userInitiated = false) {
      const normalizedIndex = (index + this.slides.length) % this.slides.length;
      if (normalizedIndex === this.current && !userInitiated) return;

      this.slides[this.current]?.classList.remove('is-active');
      this.slides[this.current]?.setAttribute('aria-hidden', 'true');
      this.dots[this.current]?.classList.remove('is-active');
      this.dots[this.current]?.setAttribute('aria-current', 'false');

      this.current = normalizedIndex;

      this.slides[this.current]?.classList.add('is-active');
      this.slides[this.current]?.setAttribute('aria-hidden', 'false');
      this.dots[this.current]?.classList.add('is-active');
      this.dots[this.current]?.setAttribute('aria-current', 'true');
      this.updateFocusableElements();

      if (userInitiated) {
        this.restart();
      } else {
        this.restartProgress();
      }
    }

    updateFocusableElements() {
      this.slides.forEach((slide, index) => {
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (index !== this.current) {
            if (!element.hasAttribute('data-koota-tabindex')) {
              element.setAttribute('data-koota-tabindex', element.getAttribute('tabindex') ?? '');
            }
            element.setAttribute('tabindex', '-1');
          } else if (element.hasAttribute('data-koota-tabindex')) {
            const originalTabindex = element.getAttribute('data-koota-tabindex');
            if (originalTabindex === '') element.removeAttribute('tabindex');
            else element.setAttribute('tabindex', originalTabindex);
            element.removeAttribute('data-koota-tabindex');
          }
        });
      });
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      this.pauseButton?.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton?.setAttribute(
        'aria-label',
        this.userPaused ? 'Relancer le carrousel' : 'Mettre le carrousel en pause'
      );
      this.syncPlayback();
    }

    syncPlayback() {
      const shouldPlay = this.autoplay
        && !this.reduceMotion.matches
        && !this.userPaused
        && !this.hoverPaused
        && !this.focusPaused
        && !document.hidden;

      this.classList.toggle('is-paused', !shouldPlay);
      this.classList.toggle('is-playing', shouldPlay);
      if (shouldPlay) this.start();
      else this.stop();
    }

    start() {
      if (this.timer) return;
      this.restartProgress();
      this.timer = window.setInterval(() => this.show(this.current + 1), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.syncPlayback();
    }

    restartProgress() {
      const activeDot = this.dots[this.current];
      if (!activeDot) return;
      const progress = activeDot.querySelector('.koota-home-hero__dot-progress');
      const animations = progress?.getAnimations?.() || [];
      animations.forEach((animation) => {
        animation.cancel();
        animation.play();
      });
    }

    onVisibilityChange() {
      this.syncPlayback();
    }

    onReducedMotionChange() {
      this.syncPlayback();
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const selectedSlide = event.target.closest('[data-koota-slide]');
      if (!selectedSlide) return;
      this.userPaused = true;
      this.pauseButton?.setAttribute('aria-pressed', 'true');
      this.show(Number(selectedSlide.dataset.index), true);
      this.syncPlayback();
    }
  }

  customElements.define('koota-home-carousel', KootaHomeCarousel);
}
